// Amazon Business ohne API: Rechnungs-PDFs, Berichts-CSV und Massendownload-ZIP importieren; zeigt, zu welchen Bestellungen noch die Rechnung fehlt.
import { useState, useEffect, useCallback, useRef } from "react";
import THEME from "../../../../theme";
import { fs } from "../../utils/styles";
import { belegeApi } from "../../../belege/belegeApi";
import { Karte, Klein, Meldung, Chip, euro, datum, datumZeit } from "../../../belege/ui";

export default function BelegeAmazon({ geheZu }) {
  const [d, setD] = useState(null);
  const [fehler, setFehler] = useState("");
  const [hinweis, setHinweis] = useState("");
  const [ergebnis, setErgebnis] = useState(null);
  const [laeuft, setLaeuft] = useState(false);
  const [zieh, setZieh] = useState(false);
  const dateiRef = useRef(null);
  const laden = useCallback(() => { belegeApi.amazonImporte().then(setD).catch(e => setFehler(e.message)); }, []);
  useEffect(() => { laden(); }, [laden]);

  const importieren = async (dateien) => {
    if (!dateien?.length) return;
    setFehler(""); setHinweis(""); setLaeuft(true);
    const fd = new FormData();
    Array.from(dateien).forEach(f => fd.append("files", f, f.name));
    try { setErgebnis((await belegeApi.amazonImport(fd)).ergebnisse); setHinweis("Import abgeschlossen."); laden(); } catch (e) { setFehler(e.message); } finally { setLaeuft(false); }
  };
  const kurz = (e) => {
    if (e.art === "fehler") return <span style={{ color: THEME.colors.danger }}>{e.fehler.join("; ")}</span>;
    if (e.art === "csv") return `${e.zeilen_neu} Zeilen neu, ${e.zeilen_bekannt} bereits bekannt${e.fehler?.length ? `, ${e.fehler.length} Zeile(n) nicht lesbar` : ""}`;
    if (e.art === "pdf") return e.dublette ? `Dublette (${e.beleg_nr})` : `Beleg ${e.beleg_nr}${e.bestellnr ? ` · Bestellung ${e.bestellnr}` : ""}${e.positionen_verknuepft ? ` · ${e.positionen_verknuepft} Position(en) verknüpft` : ""}`;
    return `${e.pdf_neu} Rechnung(en) neu, ${e.pdf_dubletten} Dublette(n), ${e.csv.length} Bericht(e)${e.uebersprungen.length ? `, ${e.uebersprungen.length} Datei(en) übersprungen` : ""}${e.fehler.length ? `, ${e.fehler.length} Fehler` : ""}`;
  };
  return (
    <div>
      <Meldung fehler={fehler} hinweis={hinweis} />
      <Karte titel="Amazon-Business-Import">
        <div onDragOver={e => { e.preventDefault(); setZieh(true); }} onDragLeave={() => setZieh(false)} onDrop={e => { e.preventDefault(); setZieh(false); importieren(e.dataTransfer.files); }}
          style={{ border: `2px dashed ${zieh ? THEME.colors.primary : THEME.colors.border}`, borderRadius: THEME.radius.lg, padding: "22px 16px", textAlign: "center" }}>
          <div style={{ marginBottom: 10 }}>Rechnungs-PDFs, Bestellbericht (CSV) oder Rechnungs-ZIP hierher ziehen</div>
          <input ref={dateiRef} type="file" multiple accept=".pdf,.csv,.zip,application/pdf,text/csv,application/zip" style={{ display: "none" }} onChange={e => { importieren(e.target.files); e.target.value = ""; }} />
          <button style={fs.btn} disabled={laeuft} onClick={() => dateiRef.current?.click()}>{laeuft ? "Importiert…" : "Dateien auswählen"}</button>
        </div>
        <Klein>Es wird keine Verbindung zu Amazon aufgebaut. Der Typ wird am Inhalt erkannt. Dieselbe Datei oder derselbe Bericht kann gefahrlos mehrfach importiert werden. PDFs werden wie jeder Beleg gelesen und mit der Bestellung aus dem Bericht verknüpft; weicht der Betrag ab, steht ein Hinweis am Beleg.</Klein>
        {ergebnis && (
          <div style={{ marginTop: 12, display: "grid", gap: 6 }}>
            {ergebnis.map((e, i) => (
              <div key={i} style={{ border: `1px solid ${THEME.colors.border}`, borderRadius: THEME.radius.md, padding: "8px 12px" }}>
                <div><Chip text={e.art} farbe={THEME.colors.textMuted} /> <strong>{e.dateiname || ""}</strong></div>
                <Klein>{kurz(e)}</Klein>
                {e.hinweise?.map((h, j) => <Klein key={j} farbe={/ABWEICHUNG/.test(h) ? THEME.colors.danger : THEME.colors.warning}>⚠ {h}</Klein>)}
                {e.uebersprungen?.length > 0 && <Klein>Übersprungen: {e.uebersprungen.join("; ")}</Klein>}
                {e.fehler?.length > 0 && e.art !== "fehler" && <Klein farbe={THEME.colors.danger}>{e.fehler.join("; ")}</Klein>}
                {e.beleg_id && !e.dublette && <button style={{ ...fs.btnGray, padding: "4px 10px", marginTop: 6 }} onClick={() => geheZu("belege", { beleg: e.beleg_id })}>Beleg öffnen</button>}
              </div>
            ))}
          </div>
        )}
      </Karte>

      <Karte titel={`Rechnung fehlt${d ? ` (${d.fehlend.length})` : ""}`}>
        <Klein>Bestellungen aus den Berichten, zu denen noch kein Rechnungs-PDF importiert wurde. Ein Bericht allein ersetzt nie den Beleg.</Klein>
        <div style={{ display: "grid", gap: 6, marginTop: 8 }}>
          {(d?.fehlend || []).map((f, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", border: `1px solid ${THEME.colors.border}`, borderRadius: THEME.radius.md, padding: "8px 12px" }}>
              <div><strong>{f.rechnungsnr || f.bestellnr}</strong>{f.rechnungsnr && f.bestellnr ? <span style={{ color: THEME.colors.textMuted }}> · {f.bestellnr}</span> : null}<Klein>{datum(f.datum)} · {f.positionen} Position(en) · {f.zahlungsart || "Zahlungsart unbekannt"}</Klein></div>
              <strong>{euro(f.brutto)}</strong>
            </div>
          ))}
          {d && d.fehlend.length === 0 && <div style={{ color: THEME.colors.success }}>Zu allen importierten Bestellungen liegt eine Rechnung vor.</div>}
        </div>
      </Karte>

      <Karte titel="Importverlauf">
        <div style={{ display: "grid", gap: 6 }}>
          {(d?.importe || []).map(i => (
            <div key={i.id} style={{ border: `1px solid ${THEME.colors.border}`, borderRadius: THEME.radius.md, padding: "8px 12px" }}>
              <div><Chip text={i.art} farbe={THEME.colors.textMuted} /> {i.dateiname || "–"} <span style={{ color: THEME.colors.textMuted }}>· {datumZeit(i.importiert_am)}</span></div>
              <Klein>{i.zeilen} Zeilen · {i.belege_neu} Beleg(e) · {i.dubletten} Dublette(n) · {i.fehler} Fehler{i.details ? ` – ${i.details}` : ""}</Klein>
            </div>
          ))}
          {d && d.importe.length === 0 && <div style={{ color: THEME.colors.textMuted }}>Noch nichts importiert.</div>}
        </div>
      </Karte>
    </div>
  );
}
